import { TableProps, TableWrapper } from '@/components/common/elements/Table';
import { IconLabel } from '@/components/common/elements/IconLabel';
import { formatDate } from '@/utils/formatDate';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';

import { designSystem } from '@/styles/theme';
import { useIsMobile } from '@/common/hooks';
import { goToHashtagSearch } from '@/pages/Post/hashtagNav';
import { UI_KIND, uiTargetAttrs } from '@/common/uiEventNames';

type Row = { id: number; title: string; nickname: string; createAt: string; viewCount: number; commentCount?: number; likeCount?: number; hashtags?: string[] };

// 메타줄(하트 오른쪽 빈자리)에 인라인 노출하는 해시태그 칩. 공간 넘치면 1개 + "+n"만.
const MetaHashtag = styled.span`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 1;
  min-width: 0;
  overflow: hidden;
  max-width: 55%;
  padding: 2px 9px;
  border-radius: 12px;
  background: #eef4f3;
  color: ${designSystem.colors.primary};
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;

  .tag {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .more {
    flex-shrink: 0;
    opacity: 0.85;
  }
`;

const PostRowWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e5e7eb;
  padding: 16px 0;
  cursor: pointer;
  transition: background-color 0.2s;
  gap: 16px;

  &:hover {
    background-color: #f9fafb;
  }

  @media (max-width: 768px) {
    padding: 11px 0;
    gap: 9px;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  min-width: 0;
`;

const TitleLine = styled.div`
  font-size: 1rem;
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 0.875rem;
    margin-bottom: 5px;
  }
`;

const MetaLine = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.875rem;
  color: #6b7280;

  @media (max-width: 768px) {
    gap: 7px;
    font-size: 0.725rem;
  }
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  flex-shrink: 0;

  @media (max-width: 768px) {
    gap: clamp(2px, 0.5vw, 3px);
  }
`;

const CommentCountArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 60px;
  height: 100%;
  padding: 8px 12px;
  background-color: #f3f4f6;
  border-radius: 8px;
  flex-shrink: 0;
  gap: 2px;

  @media (max-width: 768px) {
    min-width: 50px;
    padding: clamp(6px, 1.2vw, 8px) clamp(8px, 1.5vw, 10px);
    gap: 1px;
  }
`;

const CommentNumber = styled.span`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${designSystem.colors.primary};
  line-height: 1;

  @media (max-width: 768px) {
    font-size: clamp(0.95rem, 2vw, 1rem);
  }
`;

const CommentLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: clamp(0.65rem, 1.4vw, 0.7rem);
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.p`
  font-size: 1.1rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px 0;
`;

const EmptyDescription = styled.p`
  font-size: 0.9rem;
  color: #6b7280;
  margin: 0;
`;

const PostTable = ({ headers, data, onClick }: TableProps<Row>) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  if (!data || data.length === 0) {
    return (
      <TableWrapper>
        <EmptyState>
          <EmptyTitle>아직 게시글이 없습니다</EmptyTitle>
          <EmptyDescription>첫 번째 글을 작성해보세요!</EmptyDescription>
        </EmptyState>
      </TableWrapper>
    );
  }

  return (
    <TableWrapper>
      {data.map((row: Row) => (
        <PostRowWrapper key={row.id} {...uiTargetAttrs(UI_KIND.POST, row.id)} onClick={() => onClick(row.id)}>
          <ContentArea>
            <TitleLine>{row.title}</TitleLine>
            <MetaLine>
              <MetaItem>{row.nickname}</MetaItem>
              <MetaItem>{formatDate(row.createAt)}</MetaItem>
              <MetaItem>조회 {row.viewCount}</MetaItem>
              <MetaItem><IconLabel start="heart" size={13} gap={2}>{row.likeCount ?? 0}</IconLabel></MetaItem>
              {row.hashtags && row.hashtags.length > 0 && (
                <MetaHashtag
                  onClick={(e) => {
                    e.stopPropagation();
                    goToHashtagSearch(navigate, isMobile, row.hashtags![0]);
                  }}
                >
                  <span className="tag">#{row.hashtags[0]}</span>
                  {row.hashtags.length > 1 && <span className="more">+{row.hashtags.length - 1}</span>}
                </MetaHashtag>
              )}
            </MetaLine>
          </ContentArea>
          <CommentCountArea>
            <CommentNumber>{row.commentCount || 0}</CommentNumber>
            <CommentLabel>댓글</CommentLabel>
          </CommentCountArea>
        </PostRowWrapper>
      ))}
    </TableWrapper>
  );
};

export default PostTable;
